import React, { useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { IconButton } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Download04Icon,
  PrinterIcon,
  ViewIcon,
  ViewOffSlashIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { useTranslation } from "react-i18next";
import { formatCashFull } from "utils/formatCash";
import { PDFDownloadLink } from "@react-pdf/renderer";
import InvoicePDF from "../InvoicePDF";
import { buildInvoicePdfPalette } from "utils/invoices/invoicePdfPalette";

const FIGURE_SX = { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

interface InvoiceHeaderBoxProps {
  invoice: any;
  isInvoiceLoading?: boolean;
  showInvoice: boolean;
  onToggleInvoice: () => void;
  onPrint: () => void;
  isPrintLoading?: boolean;
}

const MathRow: React.FC<{
  label: React.ReactNode;
  figure: string;
  emphasis?: boolean;
}> = ({ label, figure, emphasis }) => (
  <Inline wrap={false} justify="between" gap="var(--space-3)">
    <Typography
      variant={emphasis ? "subtitle1" : "body1"}
      sx={emphasis ? undefined : { color: "var(--color-text-secondary)" }}
    >
      {label}
    </Typography>
    <Typography variant={emphasis ? "subtitle1" : "body1"} sx={FIGURE_SX}>
      {figure}
    </Typography>
  </Inline>
);

const InvoiceHeaderBox: React.FC<InvoiceHeaderBoxProps> = ({
  invoice,
  isInvoiceLoading,
  showInvoice,
  onToggleInvoice,
  onPrint,
  isPrintLoading,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Memoised so the document is not regenerated on every render of this panel.
  const pdfPalette = useMemo(() => buildInvoicePdfPalette(theme), [theme]);

  if (isInvoiceLoading && !invoice?.invoiceNumber) {
    return (
      <Card>
        <CardContent>
          <Inline wrap={false} justify="between" align="start" gap="0">
            <Box>
              <Skeleton variant="text" width={180} height={40} />
              <Skeleton variant="text" width={120} height={28} />
              <Skeleton variant="text" width={160} height={28} />
            </Box>
            <Inline wrap={false} gap="var(--space-1)">
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Inline>
          </Inline>
          <Skeleton variant="rectangular" width="100%" height={40} />
        </CardContent>
      </Card>
    );
  }

  if (!invoice) {
    return <></>;
  }

  const currency = invoice.currency || "USD";
  const money = (amount: number) => formatCashFull(amount, currency);
  // The figures the total is built from, so the payer can follow it to the cent.
  const adjustments = [
    { label: t("Discount"), amount: invoice.discountAmount },
    { label: t("Tax"), amount: invoice.taxAmount },
    { label: t("Service Fee"), amount: invoice.serviceFeeAmount },
  ].filter(({ amount }) => !!amount);

  return (
    <Card>
      <CardContent>
        <Stack gap="var(--space-5)">
          <Inline wrap={false} justify="between" align="start" gap="0">
            <Stack gap="var(--space-2)">
              <Typography variant="h2" sx={FIGURE_SX}>
                {money(invoice.total)}
              </Typography>
              <Typography variant="subtitle1">
                {invoice?.invoiceName}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "var(--color-text-secondary)" }}
              >
                {invoice?.invoiceNumber}
              </Typography>
            </Stack>
            <Inline wrap={false} gap="var(--space-1)">
              <Box sx={{ display: { xs: "none", md: "flex" } }}>
                <Tooltip
                  title={showInvoice ? t("Hide Invoice") : t("Show Invoice")}
                >
                  <span>
                    <IconButton
                      variant="ghost"
                      size="small"
                      aria-label={
                        showInvoice ? t("Hide Invoice") : t("Show Invoice")
                      }
                      onClick={onToggleInvoice}
                      disabled={isInvoiceLoading}
                    >
                      <HugeiconsIcon
                        icon={showInvoice ? ViewOffSlashIcon : ViewIcon}
                        size={18}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Tooltip title={t("Print")}>
                <span>
                  <IconButton
                    variant="ghost"
                    size="small"
                    aria-label={t("Print")}
                    onClick={onPrint}
                    disabled={isPrintLoading || isInvoiceLoading}
                  >
                    <HugeiconsIcon icon={PrinterIcon} size={18} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t("Download PDF")}>
                <span>
                  <PDFDownloadLink
                    document={
                      <InvoicePDF invoice={invoice} palette={pdfPalette} />
                    }
                    fileName={`Invoice-${invoice.invoiceNumber}.pdf`}
                    style={{ color: "inherit" }}
                  >
                    {({ loading }) => (
                      <IconButton
                        variant="ghost"
                        size="small"
                        aria-label={t("Download PDF")}
                        disabled={loading || isInvoiceLoading}
                      >
                        <HugeiconsIcon icon={Download04Icon} size={18} />
                      </IconButton>
                    )}
                  </PDFDownloadLink>
                </span>
              </Tooltip>
            </Inline>
          </Inline>

          <Divider />

          <Stack gap="var(--space-2)">
            {invoice?.details?.length > 5 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                onClick={onToggleInvoice}
                sx={{ cursor: "pointer" }}
              >
                {t("View full invoice for all items")}
              </Typography>
            ) : (
              invoice?.details?.map((item: any, idx: number) => (
                <MathRow
                  key={idx}
                  label={item.productOrService}
                  figure={money(item.total)}
                />
              ))
            )}
            {adjustments.length > 0 && (
              <>
                <MathRow
                  label={t("Subtotal")}
                  figure={money(invoice.subtotal)}
                />
                {adjustments.map(({ label, amount }) => (
                  <MathRow key={label} label={label} figure={money(amount)} />
                ))}
              </>
            )}
          </Stack>

          <Divider />

          <MathRow emphasis label={t("Total")} figure={money(invoice.total)} />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default InvoiceHeaderBox;
